import Seo from "@/components/Seo";
import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, Download } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/motion";
import useApi from "@/hooks/useApi";
import usePreviewDraft from "@/hooks/usePreviewDraft";
import PreviewBanner from "@/components/PreviewBanner";
import { BRAND_DETAILS, resolveBrandDetail } from "@/content/brands";
import { AdGuidelines } from "@/components/AdGuidelines";
import InstagramFeed from "@/components/InstagramFeed";

const EASE = [0.16, 1, 0.3, 1];

const FreeReports = ({ reports }) => (
  <section data-testid="free-reports" className="bg-secondary">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
      <FadeUp>
        <p className="kicker mb-4">Free Reports</p>
        <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-3xl">
          Intelligence you can download today
        </h2>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-foreground/80">
          A selection of our published research and briefings — free to download. Independent, data-led
          and built for the people who shape education in the region.
        </p>
      </FadeUp>

      <Stagger className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {reports.map((r) => (
          <StaggerItem key={r.file}>
            <a
              href={r.file}
              download
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`report-${r.file.split("/").pop()}`}
              className="group block bg-white rounded-2xl overflow-hidden border border-border hover:shadow-xl transition-shadow duration-300 h-full"
            >
              <div className="ed-img aspect-[3/4] bg-white border-b border-border">
                <img src={r.cover} alt={`${r.title} cover`} className="!object-contain" />
              </div>
              <div className="p-5">
                <h3 className="headline text-lg text-ink leading-tight group-hover:text-primary transition-colors">
                  {r.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/70">{r.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-2 font-sans text-sm font-bold text-primary">
                  <Download size={15} /> Download PDF
                </span>
              </div>
            </a>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const AccessSteps = ({ access }) => (
  <section data-testid="access-steps" className="bg-secondary">
    <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
      <FadeUp>
        <p className="kicker mb-4">Getting Started</p>
        <h2 className="headline text-4xl md:text-6xl uppercase text-ink max-w-3xl">{access.title}</h2>
        {access.intro && <p className="mt-6 max-w-3xl text-lg leading-relaxed text-foreground/80">{access.intro}</p>}
      </FadeUp>

      <Stagger className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {access.steps.map((s, i) => (
          <StaggerItem key={i}>
            <div className="flex gap-5 bg-white rounded-2xl border border-border p-6 md:p-7 h-full" data-testid={`access-step-${i + 1}`}>
              <span className="headline-xl text-4xl md:text-5xl text-primary/30 leading-none shrink-0">{i + 1}</span>
              <p className="text-[16px] md:text-lg leading-relaxed text-foreground/80">
                {s.text}
                {s.url && (
                  <>
                    {" "}
                    <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`access-step-link-${i + 1}`} className="font-semibold text-primary underline underline-offset-2 hover:text-ink transition-colors break-all">
                      {s.urlLabel || s.url}
                    </a>
                  </>
                )}
              </p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);


const BrandDetail = () => {
  const { slug } = useParams();
  const baseDetail = BRAND_DETAILS[slug];
  const { data: brands } = useApi("/brands");
  const { draft, isPreview } = usePreviewDraft();

  if (!baseDetail) {
    return (
      <div className="pt-[140px] pb-40 text-center">
        <p className="headline text-4xl text-ink">Brand not found</p>
        <Link to="/brands" className="mt-6 inline-block text-primary font-bold">← Back to brands</Link>
      </div>
    );
  }

  const dbBrand = (brands || []).find((b) => b.name === baseDetail.match) || {};
  // Preview overlay: draft values from the CMS win over the DB values, so admins
  // can sanity-check unsaved changes at /brands/<slug>?preview=<key>.
  const brand = isPreview ? { ...dbBrand, ...draft } : dbBrand;
  const comingSoon = brand.status === "coming_soon";
  const detail = resolveBrandDetail(brand, baseDetail);

  return (
    <div data-testid="brand-detail-page" className={isPreview ? "pt-[48px]" : undefined}>
      {isPreview && <PreviewBanner label="Brand draft preview" />}
      <Seo
        title={brand.domain || brand.name || "Brand"}
        path={`/brands/${slug || ""}`}
        description={brand.description || detail.body?.[0] || undefined}
        image={detail.hero_image || (detail.hero_images && detail.hero_images[0]) || brand.image}
      />
      {/* Masthead */}
      <section className="pt-[76px]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 pt-12 md:pt-16 pb-8">
          <Link to="/brands" className="inline-flex items-center gap-2 text-sm font-bold text-foreground/60 hover:text-primary transition-colors mb-8">
            <ArrowLeft size={16} /> All brands
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <span className="kicker">{brand.category}</span>
            {comingSoon && (
              <span className="font-sans text-[10px] font-extrabold uppercase tracking-wide bg-primary text-primary-foreground px-2.5 py-1 rounded-full">
                Coming Soon
              </span>
            )}
          </div>
          {brand.logo ? (
            <div className="inline-flex items-center mb-6">
              <img src={brand.logo} alt={brand.name} className={`w-auto object-contain ${brand.name === "EdinformaPlus" ? "h-7 md:h-10" : "h-14 md:h-20"}`} />
            </div>
          ) : (
            <h1 className="headline-xl uppercase text-4xl sm:text-5xl md:text-6xl text-ink mb-4 break-words max-w-full">{detail.match}</h1>
          )}
        </div>

        {/* Hero image(s) */}
        <FadeUp>
          {detail.hero_images ? (
            <div className="grid grid-cols-3 gap-2 md:gap-4 bg-secondary/50">
              {detail.hero_images.map((src, i) => (
                <div key={i} className="ed-img group aspect-[3/4] max-h-[560px]">
                  <img src={src} alt={`${brand.name} report ${i + 1}`} className="!object-cover !object-top" />
                </div>
              ))}
            </div>
          ) : (
            <div className="ed-img group aspect-[21/9] md:aspect-[21/8] max-h-[540px]">
              <motion.div
                className="w-full h-full"
                style={{ transformOrigin: detail.hero_animation?.origin || "center" }}
                initial={{ scale: 1 }}
                animate={{ scale: detail.hero_animation?.scale || 1.08 }}
                transition={{ duration: detail.hero_animation?.duration || 8, ease: "easeInOut", repeat: Infinity, repeatType: "mirror" }}
              >
                <img
                  src={detail.hero_image || brand.image}
                  alt={brand.name}
                  onError={(e) => {
                    if (brand.image && e.currentTarget.src !== brand.image) e.currentTarget.src = brand.image;
                  }}
                />
              </motion.div>
            </div>
          )}
        </FadeUp>
      </section>

      {/* Stats */}
      <section data-testid="brand-stats" className="bg-ink text-white">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-14 md:py-20">
          <Stagger className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {detail.stats.map((s, i) => (
              <StaggerItem key={i} className="border-t-2 border-white/20 pt-5">
                <p className="headline-xl text-5xl md:text-6xl text-primary">{s.value}</p>
                <p className="mt-3 text-base font-bold">{s.label}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Body copy */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <FadeUp>
            <p className="kicker mb-4">About the brand</p>
            <h2 className="headline text-3xl md:text-4xl uppercase text-ink leading-[1.05]">{brand.tagline}</h2>
          </FadeUp>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <div className="space-y-6 text-lg leading-relaxed text-foreground/80">
            {detail.body.map((para, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "0px 0px -8% 0px" }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.04 }}
              >
                {para}
              </motion.p>
            ))}
          </div>
          {brand.url && !comingSoon && (
            <FadeUp>
              <a
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="brand-visit-link"
                className="mt-10 inline-flex items-center gap-2 font-sans text-base font-bold bg-primary text-primary-foreground rounded-full pl-7 pr-5 py-4 hover:bg-ink transition-colors"
              >
                Visit {brand.domain} <ArrowUpRight size={18} />
              </a>
            </FadeUp>
          )}
          {!brand.url && !comingSoon && (
            <FadeUp>
              <Link
                to={`/contact?role=${encodeURIComponent(detail.match + " enquiry")}`}
                data-testid="brand-visit-link"
                className="mt-10 inline-flex items-center gap-2 font-sans text-base font-bold bg-primary text-primary-foreground rounded-full pl-7 pr-5 py-4 hover:bg-ink transition-colors"
              >
                Enquire about {detail.match} <ArrowRight size={18} />
              </Link>
            </FadeUp>
          )}
          {comingSoon && (
            <FadeUp>
              <Link
                to="/contact"
                data-testid="brand-register-interest"
                className="mt-10 inline-flex items-center gap-2 font-sans text-base font-bold bg-primary text-primary-foreground rounded-full pl-7 pr-5 py-4 hover:bg-ink transition-colors"
              >
                Register your interest <ArrowRight size={18} />
              </Link>
            </FadeUp>
          )}
        </div>
      </section>

      {detail.access && <AccessSteps access={detail.access} />}
      {detail.instagram && detail.instagram.length > 0 && (
        <InstagramFeed entries={detail.instagram} brandName={brand.name} />
      )}
      {detail.hero_video && (
        <section data-testid="brand-video" className="bg-ink">
          <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
            <FadeUp>
              <p className="kicker mb-4">Watch the film</p>
              <h2 className="headline text-3xl md:text-5xl uppercase text-white mb-8 max-w-3xl">
                Inside the region&rsquo;s most prestigious education awards
              </h2>
              <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black">
                <iframe
                  src={detail.hero_video}
                  title={`${detail.match} film`}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </FadeUp>
          </div>
        </section>
      )}

      {detail.reports && <FreeReports reports={detail.reports} />}
      {detail.ad_guidelines && <AdGuidelines g={detail.ad_guidelines} brandName={brand.domain || detail.match} showJumbo={false} />}
    </div>
  );
};

export default BrandDetail;
